import { App, Form, Input, InputNumber, Select } from "antd";
import type { Dayjs } from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { BsDashCircle } from "react-icons/bs";
import { UNIT_ICON, VEHICLE_UNIT_ICON } from "src/assets/svgs";
import {
  CustomButton,
  CustomInput,
  CustomSelect,
  CustomSharedDatePicker,
  CustomSwitch,
  CustomTypography,
} from "sipli-fleet-core/components";
import {
  useMutationInstallUnit,
  useQueryGetUnitManagementAdjusterLookup,
  useQueryGetUnitManagementClientLookup,
  useQueryGetUnitManagementVehicleMakeLookup,
  useQueryGetUnitManagementVehicleModelLookup,
  useQueryGetUnitManagementVehicleTypeLookup,
  useQuerySearchUnitByImeiOrSerial,
} from "src/queries/unitManagement.queries";
import type { UnitManagementLookupItem } from "src/services/unitManagementService";
import { useSelectDistributor } from "src/store/selectDistributorStore";
import { AddFuelSensorModal } from "../components/DeviceManagementModals";
import "./Install.css";

type Option = {
  label: string;
  value: string | number;
};

interface InstallFormValues {
  licensePlate?: string;
  vin?: string;
  vehicleMakeId?: string | number;
  vehicleModelId?: string | number;
  vehicleTypeId?: string | number;
  odometer?: number;
  motorHours?: number;
  firstRegistrationDate?: Dayjs;
  vehicleNotes?: string;
  adjusterId?: string | number;
  customerId?: string | number;
  deviceId?: string | number;
  withTpms?: boolean;
  unitNotes?: string;
}

interface InstallPanelProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  children: ReactNode;
}

const MIN_SEARCH_LENGTH = 3;

const getFieldValue = (item: UnitManagementLookupItem): string | number | undefined => {
  const value = item.id ?? item.value;
  return typeof value === "string" || typeof value === "number" ? value : undefined;
};

const getItemLabel = (item: UnitManagementLookupItem) => {
  const surname = typeof item.surname === "string" ? item.surname : "";
  const fullName = [item.name, surname].filter(Boolean).join(" ");
  const fallback =
    item.companyName ||
    item.label ||
    item.title ||
    fullName ||
    item.code ||
    item.imei ||
    item.serial;

  return String(fallback ?? "");
};

const getUnitLabel = (item: UnitManagementLookupItem) => {
  const imei = item.imei ? `IMEI: ${item.imei}` : "";
  const serial = item.serial ? `Serial: ${item.serial}` : "";
  const label = [imei, serial].filter(Boolean).join(" - ");

  return label || getItemLabel(item);
};

const pickArray = (value: unknown): UnitManagementLookupItem[] => {
  if (!Array.isArray(value)) return [];

  return value.filter(
    (item): item is UnitManagementLookupItem => !!item && typeof item === "object"
  );
};

const extractLookupItems = (value: unknown): UnitManagementLookupItem[] => {
  if (Array.isArray(value)) {
    const first = value[0];

    if (first && typeof first === "object") {
      const nested = first as Record<string, unknown>;
      const nestedResponse = pickArray(nested.response);

      if (nestedResponse.length > 0) return nestedResponse;
    }

    return pickArray(value);
  }

  if (!value || typeof value !== "object") return [];

  const record = value as Record<string, unknown>;
  const responseItems = pickArray(record.response);
  if (responseItems.length > 0) return responseItems;

  if (record.response && typeof record.response === "object") {
    const nestedResponse = record.response as Record<string, unknown>;
    const nestedDataItems = pickArray(nestedResponse.data);
    if (nestedDataItems.length > 0) return nestedDataItems;

    const nestedSpeedItems = pickArray(nestedResponse.speeds);
    if (nestedSpeedItems.length > 0) return nestedSpeedItems;

    const nestedContentItems = pickArray(nestedResponse.content);
    if (nestedContentItems.length > 0) return nestedContentItems;
  }

  const dataItems = pickArray(record.data);
  if (dataItems.length > 0) return dataItems;

  const speedItems = pickArray(record.speeds);
  if (speedItems.length > 0) return speedItems;

  return pickArray(record.content);
};

const toOptions = (items: UnitManagementLookupItem[]): Option[] =>
  items
    .map((item) => {
      const value = getFieldValue(item);
      const label = getItemLabel(item);

      if (value === undefined || !label) return null;

      return { value, label };
    })
    .filter((option): option is Option => !!option);

const toUnitOptions = (items: UnitManagementLookupItem[]): Option[] =>
  items.reduce<Option[]>((options, item) => {
    const value = getFieldValue(item);
    const label = getUnitLabel(item);

    if (value === undefined || !label) return options;

    return [...options, { value, label }];
  }, []);

const getQueryData = (value: unknown) => {
  if (!value || typeof value !== "object" || !("data" in value)) {
    return undefined;
  }

  return (value as { data?: unknown }).data;
};

const numberOrUndefined = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const getErrorMessage = (error: unknown) => {
  if (!error || typeof error !== "object") return undefined;

  const response = (error as { response?: unknown }).response;
  if (!response || typeof response !== "object") return undefined;

  const data = (response as { data?: unknown }).data;
  if (!data || typeof data !== "object") return undefined;

  const message = (data as { message?: unknown }).message;
  return typeof message === "string" ? message : undefined;
};

const InstallPanel = ({ icon, title, description, children }: InstallPanelProps) => (
  <div className="device-install-panel">
    <div className="device-install-panel-header">
      <div className="device-install-panel-title-row">
        {icon}
        <CustomTypography.CustomText className="device-install-panel-title">
          {title}
        </CustomTypography.CustomText>
      </div>
      <CustomTypography.CustomText className="device-install-panel-description">
        {description}
      </CustomTypography.CustomText>
    </div>
    <div className="device-install-divider" />
    {children}
  </div>
);

const Install = () => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<InstallFormValues>();
  const selectedVehicleMakeId = Form.useWatch("vehicleMakeId", form);
  const selectedDeviceId = Form.useWatch("deviceId", form);
  const { selectedDistributor } = useSelectDistributor();
  const [deviceSearch, setDeviceSearch] = useState("");
  const [fuelSensorSearch, setFuelSensorSearch] = useState("");
  const [fuelSensorModalOpen, setFuelSensorModalOpen] = useState(false);
  const [selectedFuelSensorId, setSelectedFuelSensorId] = useState<string | number | undefined>();
  const [fuelSensors, setFuelSensors] = useState<UnitManagementLookupItem[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<UnitManagementLookupItem | null>(null);
  const didMountVehicleMakeRef = useRef(false);
  const distributorId = selectedDistributor ?? undefined;

  const { data: vehicleMakeData, isLoading: isVehicleMakeLoading } =
    useQueryGetUnitManagementVehicleMakeLookup();
  const { data: vehicleModelData, isLoading: isVehicleModelLoading } =
    useQueryGetUnitManagementVehicleModelLookup(
      selectedVehicleMakeId,
      {},
      {
        enabled: !!selectedVehicleMakeId,
      }
    );
  const { data: vehicleTypeData, isLoading: isVehicleTypeLoading } =
    useQueryGetUnitManagementVehicleTypeLookup();

  const { data: adjusterData, isLoading: isAdjusterLoading } =
    useQueryGetUnitManagementAdjusterLookup(distributorId ? { distributorId } : {}, {
      enabled: !!distributorId,
    });
  const { data: clientData, isLoading: isClientLoading } = useQueryGetUnitManagementClientLookup(
    distributorId ? { distributorId } : {},
    { enabled: !!distributorId }
  );
  const { data: deviceSearchData, isLoading: isDeviceSearchLoading } =
    useQuerySearchUnitByImeiOrSerial({
      query: deviceSearch,
      unitType: "DVC",
      distributorId,
    });
  const { data: fuelSensorSearchData, isLoading: isFuelSensorSearchLoading } =
    useQuerySearchUnitByImeiOrSerial({
      query: fuelSensorSearch,
      unitType: "FUESNSR",
      distributorId,
    });
  const { mutateAsync: installUnit, isPending: isInstalling } = useMutationInstallUnit();

  const vehicleMakeOptions = useMemo(
    () => toOptions(extractLookupItems(getQueryData(vehicleMakeData))),
    [vehicleMakeData]
  );
  const vehicleModelOptions = useMemo(
    () => toOptions(extractLookupItems(getQueryData(vehicleModelData))),
    [vehicleModelData]
  );
  const vehicleTypeOptions = useMemo(
    () => toOptions(extractLookupItems(getQueryData(vehicleTypeData))),
    [vehicleTypeData]
  );
  const adjusterOptions = useMemo(
    () => toOptions(extractLookupItems(getQueryData(adjusterData))),
    [adjusterData]
  );
  const clientOptions = useMemo(
    () => toOptions(extractLookupItems(getQueryData(clientData))),
    [clientData]
  );
  const deviceItems = useMemo(
    () => extractLookupItems(getQueryData(deviceSearchData)),
    [deviceSearchData]
  );
  const deviceOptions = useMemo(() => toUnitOptions(deviceItems), [deviceItems]);
  const fuelSensorItems = useMemo(
    () => extractLookupItems(getQueryData(fuelSensorSearchData)),
    [fuelSensorSearchData]
  );
  const fuelSensorOptions = useMemo(() => toUnitOptions(fuelSensorItems), [fuelSensorItems]);

  useEffect(() => {
    if (!didMountVehicleMakeRef.current) {
      didMountVehicleMakeRef.current = true;
      return;
    }

    form.setFieldValue("vehicleModelId", undefined);
  }, [form, selectedVehicleMakeId]);

  useEffect(() => {
    if (!selectedDeviceId) {
      setSelectedDevice(null);
      return;
    }

    const device = deviceItems.find((item) => getFieldValue(item) === selectedDeviceId);

    if (device) {
      setSelectedDevice(device);
    }
  }, [deviceItems, selectedDeviceId]);

  const handleAddFuelSensor = (value: string | number) => {
    const sensor = fuelSensorItems.find((item) => getFieldValue(item) === value);
    if (!sensor) return;

    setFuelSensors((prevSensors) => {
      if (prevSensors.some((item) => getFieldValue(item) === value)) {
        return prevSensors;
      }

      return [...prevSensors, sensor];
    });
    setFuelSensorModalOpen(false);
    setSelectedFuelSensorId(undefined);
    setFuelSensorSearch("");
  };

  const handleConfirmAddFuelSensor = () => {
    if (selectedFuelSensorId === undefined) return;
    handleAddFuelSensor(selectedFuelSensorId);
  };

  const handleCloseFuelSensorModal = () => {
    setFuelSensorModalOpen(false);
    setSelectedFuelSensorId(undefined);
    setFuelSensorSearch("");
  };

  const handleRemoveFuelSensor = (value: string | number | undefined) => {
    setFuelSensors((prevSensors) => prevSensors.filter((item) => getFieldValue(item) !== value));
  };

  const handleReset = () => {
    form.resetFields();
    setDeviceSearch("");
    setFuelSensorSearch("");
    setFuelSensors([]);
    setSelectedDevice(null);
    setFuelSensorModalOpen(false);
    setSelectedFuelSensorId(undefined);
  };

  const handleFinish = async (values: InstallFormValues) => {
    const deviceId = selectedDevice?.id ?? values.deviceId;
    const payload = {
      vehicleDetail: {
        adjusterId: values.adjusterId,
        clientId: values.customerId,
        modelId: values.vehicleModelId,
        odometer: numberOrUndefined(values.odometer),
        vin: values.vin,
        registrationDate: values.firstRegistrationDate?.toISOString(),
        motorHours: numberOrUndefined(values.motorHours),
        vehicleTypeId: values.vehicleTypeId,
        licensePlate: values.licensePlate,
        notes: values.vehicleNotes,
        active: true,
      },
      deviceDetail: {
        id: deviceId,
        deviceId,
        notes: values.unitNotes,
        ...(distributorId ? { distributorId } : {}),
        withTpms: Boolean(values.withTpms),
      },
      fuelTankAndUnit: fuelSensors
        .map((sensor) => getFieldValue(sensor))
        .filter((value): value is string | number => value !== undefined)
        .map((fuelSensorUnitId) => ({ fuelSensorUnitId })),
      insurance: [],
      accessories: [],
      consumableResources: [],
    };

    try {
      await installUnit(payload);
      message.success(t("unit_installed_successfully"));
      handleReset();
    } catch (error) {
      message.error(getErrorMessage(error) ?? t("unit_install_failed"));
    }
  };

  return (
    <div className="device-install-card">
      <CustomTypography.CustomTitle className="device-install-title">
        {t("install_unit")}
      </CustomTypography.CustomTitle>

      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ withTpms: false }}
        onFinish={handleFinish}
        className="device-install-form"
      >
        <div className="device-install-panels">
          <InstallPanel
            icon={<img src={VEHICLE_UNIT_ICON} alt="" className="device-install-panel-icon" />}
            title={t("vehicle_details")}
            description={t("vehicle_details_install_description")}
          >
            <div className="grid gap-x-2 md:grid-cols-2">
              <Form.Item
                name="licensePlate"
                label={`${t("license_plate")}*`}
                rules={[{ required: true, message: t("field_required") }]}
              >
                <CustomInput
                  placeholder={t("type")}
                  maxLength={10}
                  className="!mt-0 !h-8 !rounded-[4px]"
                />
              </Form.Item>
              <Form.Item
                name="vin"
                label={`${t("vin")}*`}
                rules={[{ required: true, message: t("field_required") }]}
              >
                <CustomInput
                  placeholder={t("placeholder")}
                  maxLength={25}
                  className="!mt-0 !h-8 !rounded-[4px]"
                />
              </Form.Item>
              <Form.Item
                name="vehicleMakeId"
                label={`${t("vehicle_brand")}*`}
                rules={[{ required: true, message: t("field_required") }]}
              >
                <CustomSelect
                  placeholder={t("select")}
                  options={vehicleMakeOptions}
                  loading={isVehicleMakeLoading}
                  className="!h-8"
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
              <Form.Item name="vehicleModelId" label={t("vehicle_model")}>
                <CustomSelect
                  placeholder={t("select")}
                  options={vehicleModelOptions}
                  loading={isVehicleModelLoading}
                  disabled={!selectedVehicleMakeId}
                  className="!h-8"
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
              <Form.Item
                name="vehicleTypeId"
                label={`${t("vehicle_type")}*`}
                rules={[{ required: true, message: t("field_required") }]}
              >
                <CustomSelect
                  placeholder={t("select")}
                  options={vehicleTypeOptions}
                  loading={isVehicleTypeLoading}
                  className="!h-8"
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
              <Form.Item name="odometer" label={t("odometer_gps")}>
                <InputNumber
                  min={0}
                  className="!h-8 !w-full !rounded-[4px]"
                  placeholder={t("type")}
                />
              </Form.Item>
              <Form.Item name="motorHours" label={t("motor_hours")}>
                <InputNumber
                  min={0}
                  className="!h-8 !w-full !rounded-[4px]"
                  placeholder={t("type")}
                />
              </Form.Item>
              <Form.Item name="firstRegistrationDate" label={t("first_registration_date")}>
                <CustomSharedDatePicker
                  placeholder={t("select_date")}
                  className="!h-8 !rounded-[4px]"
                />
              </Form.Item>
            </div>
            <Form.Item name="vehicleNotes" label={t("notes")}>
              <Input.TextArea
                placeholder={t("written_text")}
                className="!min-h-[72px] !rounded-[4px]"
              />
            </Form.Item>
          </InstallPanel>

          <InstallPanel
            icon={<img src={UNIT_ICON} alt="" className="device-install-panel-icon" />}
            title={t("unit_details")}
            description={t("unit_details_install_description")}
          >
            <div className="grid gap-x-2 md:grid-cols-2">
              <Form.Item
                name="adjusterId"
                label={`${t("adjuster")}*`}
                rules={[{ required: true, message: t("field_required") }]}
              >
                <CustomSelect
                  placeholder={t("select")}
                  options={adjusterOptions}
                  loading={isAdjusterLoading}
                  disabled={!distributorId}
                  className="!h-8"
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
              <Form.Item
                name="customerId"
                label={`${t("customer")}*`}
                rules={[{ required: true, message: t("field_required") }]}
              >
                <CustomSelect
                  placeholder={t("select")}
                  options={clientOptions}
                  loading={isClientLoading}
                  disabled={!distributorId}
                  className="!h-8"
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </div>
            <Form.Item
              name="deviceId"
              label={`${t("device")}*`}
              rules={[{ required: true, message: t("field_required") }]}
            >
              <Select
                showSearch
                filterOption={false}
                placeholder={t("search_by_imei_or_serial")}
                options={deviceOptions}
                loading={isDeviceSearchLoading}
                disabled={!distributorId}
                className="!h-8"
                onSearch={setDeviceSearch}
                onSelect={(value) => {
                  const device = deviceItems.find((item) => getFieldValue(item) === value);
                  setSelectedDevice(device ?? null);
                }}
                onClear={() => setSelectedDevice(null)}
                notFoundContent={
                  deviceSearch.trim().length < MIN_SEARCH_LENGTH
                    ? t("type_at_least_3_characters")
                    : t("no_data")
                }
              />
            </Form.Item>

            <Form.Item className="!mb-3">
              <div className="flex items-center gap-2">
                <Form.Item name="withTpms" valuePropName="checked" noStyle>
                  <CustomSwitch />
                </Form.Item>
                <CustomTypography.CustomText>{t("add_tpms")}</CustomTypography.CustomText>
              </div>
            </Form.Item>

            <Form.Item name="unitNotes" label={t("notes")}>
              <Input.TextArea
                placeholder={t("written_text")}
                className="!min-h-[72px] !rounded-[4px]"
              />
            </Form.Item>

            <div className="device-install-fuel-sensors">
              <div className="device-install-fuel-header">
                <CustomTypography.CustomText className="device-install-fuel-title">
                  {t("fuel_sensors")}
                </CustomTypography.CustomText>
                <CustomButton
                  type="primary"
                  buttonSize="small"
                  label={t("add_fuel_sensor")}
                  disabled={!distributorId}
                  className="device-install-add-fuel-btn !text-white"
                  onClick={() => setFuelSensorModalOpen(true)}
                />
              </div>

              <div className="flex flex-col gap-2">
                {fuelSensors.length > 0 ? (
                  fuelSensors.map((sensor) => {
                    const value = getFieldValue(sensor);

                    return (
                      <div key={String(value)} className="device-install-fuel-sensor-row">
                        <span>
                          {t("associate_unit")}: {getUnitLabel(sensor)}
                        </span>
                        <button
                          type="button"
                          className="device-install-remove-fuel-btn"
                          onClick={() => handleRemoveFuelSensor(value)}
                          aria-label="Remove fuel sensor"
                        >
                          <BsDashCircle />
                        </button>
                      </div>
                    );
                  })
                ) : (
                  <CustomTypography.CustomText className="!text-sm !text-gray7">
                    {t("no_fuel_sensors_added")}
                  </CustomTypography.CustomText>
                )}
              </div>
            </div>
          </InstallPanel>
        </div>

        <div className="device-install-actions">
          <CustomButton
            type="secondary"
            tone="default"
            danger={false}
            label={t("reset")}
            buttonSize="big"
            onClick={handleReset}
            disabled={isInstalling}
            className="device-install-reset-btn"
          />
          <CustomButton
            type="primary"
            label={t("install_unit")}
            buttonSize="big"
            htmlType="submit"
            loading={isInstalling}
            className="device-install-submit-btn !text-white"
          />
        </div>
      </Form>

      <AddFuelSensorModal
        open={fuelSensorModalOpen}
        options={fuelSensorOptions}
        loading={isFuelSensorSearchLoading}
        value={selectedFuelSensorId}
        onSearch={setFuelSensorSearch}
        onChange={setSelectedFuelSensorId}
        onCancel={handleCloseFuelSensorModal}
        onConfirm={handleConfirmAddFuelSensor}
        notFoundContent={
          fuelSensorSearch.trim().length < MIN_SEARCH_LENGTH
            ? t("type_at_least_3_characters")
            : t("no_data")
        }
      />
    </div>
  );
};

export default Install;
